import { useNavigate } from 'react-router-dom'
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Box,
    IconButton,
    Tooltip,
} from '@mui/material'
import { Visibility, Email, Send, Comment } from '@mui/icons-material'
import { useTranslation } from '@i18n'
import { useSubmissions } from '@hooks/useSubmissions'
import { useAuthStore } from '@store/authStore'
import { formatRelativeTime, getBilingualValue } from '@utils/helpers'
import StatusChip from '@components/Common/StatusChip'
import Loading from '@components/Common/Loading'

const RecentSubmissions = () => {
    const { t, language } = useTranslation()
    const navigate = useNavigate()
    const { user } = useAuthStore()
    const isDepartmentUser = user?.user_type === 'department'

    // Get recent 5 submissions
    const { submissions, isLoading } = useSubmissions()
    const recentSubmissions = submissions.slice(0, 5)

    if (isLoading) {
        return <Loading message={t('common.loading')} />
    }

    if (recentSubmissions.length === 0) {
        return (
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                <Typography color="text.secondary">
                    {t('submissions.noSubmissions')}
                </Typography>
            </Paper>
        )
    }

    const getFieldPreview = (data) => {
        if (!data) return '-'
        const fields = Object.entries(data).slice(0, 2)
        return fields.map(([key, value]) => `${value}`).join(', ')
    }

    return (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>{t('submissions.submissionId')}</TableCell>
                        <TableCell>Preview</TableCell>
                        <TableCell>{t('submissions.status')}</TableCell>
                        <TableCell>{t('submissions.date')}</TableCell>
                        <TableCell align="right">{t('common.actions')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {recentSubmissions.map((submission) => (
                        <TableRow
                            key={submission.id}
                            hover
                            sx={{ cursor: 'pointer' }}
                            onClick={() => navigate(`/submissions/${submission.id}`)}
                        >
                            <TableCell>
                                <Typography variant="body2" fontWeight={600}>
                                    {submission.submission_id}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                        maxWidth: 200,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {getFieldPreview(submission.data)}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <StatusChip status={submission.status} />
                            </TableCell>
                            <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                    {formatRelativeTime(submission.created_at, language)}
                                </Typography>
                            </TableCell>
                            <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                                <Tooltip title={language === 'ar' ? 'عرض' : 'View'}>
                                    <IconButton
                                        size="small"
                                        onClick={() => navigate(`/submissions/${submission.id}`)}
                                    >
                                        <Visibility />
                                    </IconButton>
                                </Tooltip>
                                {isDepartmentUser ? (
                                    <Tooltip title={language === 'ar' ? 'إضافة تعليق' : 'Add Comment'}>
                                        <IconButton
                                            size="small"
                                            color="secondary"
                                            onClick={() => navigate(`/submissions/${submission.id}?tab=comments`)}
                                        >
                                            <Comment />
                                        </IconButton>
                                    </Tooltip>
                                ) : (
                                    <>
                                        <Tooltip title={language === 'ar' ? 'إرسال بريد' : 'Send Email'}>
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() => navigate(`/submissions/${submission.id}?tab=email`)}
                                            >
                                                <Email />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={language === 'ar' ? 'تحويل للقسم' : 'Navigate to Department'}>
                                            <IconButton
                                                size="small"
                                                sx={{ color: '#1a3a6b' }}
                                                onClick={() => navigate(`/submissions/${submission.id}?tab=navigate`)}
                                            >
                                                <Send />
                                            </IconButton>
                                        </Tooltip>
                                    </>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

export default RecentSubmissions

